import { useRef, useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { recipeData, RecipeItem } from './mockData'

interface RecipeSectionProps {
  onUseRecipe?: (item: RecipeItem) => void
}

export default function RecipeSection({ onUseRecipe }: RecipeSectionProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(true)

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setShowLeftArrow(scrollLeft > 10)
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10)
    }
  }

  useEffect(() => {
    checkScroll()
    window.addEventListener('resize', checkScroll)
    return () => window.removeEventListener('resize', checkScroll)
  }, [])

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400
      const newScrollLeft =
        scrollContainerRef.current.scrollLeft +
        (direction === 'left' ? -scrollAmount : scrollAmount)
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      })
      setTimeout(checkScroll, 300)
    }
  }

  return (
    <div className="mb-8">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Recipe</h2>
      </div>

      <div className="group/list relative">
        {/* Gradient Masks */}
        <div
          className={`pointer-events-none absolute bottom-4 left-0 top-0 z-10 w-20 bg-gradient-to-r from-[#F4F4F5] to-transparent transition-opacity duration-300 ${
            showLeftArrow ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div
          className={`pointer-events-none absolute bottom-4 right-0 top-0 z-10 w-20 bg-gradient-to-l from-[#F4F4F5] to-transparent transition-opacity duration-300 ${
            showRightArrow ? 'opacity-100' : 'opacity-0'
          }`}
        />

        <button
          onClick={() => scroll('left')}
          className={`absolute -left-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-gray-400 text-white shadow-lg transition-all hover:bg-black cursor-pointer ${
            showLeftArrow ? 'opacity-0 group-hover/list:opacity-100' : 'pointer-events-none opacity-0'
          }`}
          aria-label="Scroll left"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>

        <div
          ref={scrollContainerRef}
          onScroll={checkScroll}
          className="scrollbar-hide flex gap-4 overflow-x-auto pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {recipeData.map((item, index) => (
            <RecipeCard key={item.id} item={item} isNew={index < 2} onUse={() => onUseRecipe?.(item)} />
          ))}
        </div>

        <button
          onClick={() => scroll('right')}
          className={`absolute -right-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-gray-400 text-white shadow-lg transition-all hover:bg-black cursor-pointer ${
            showRightArrow ? 'opacity-0 group-hover/list:opacity-100' : 'pointer-events-none opacity-0'
          }`}
          aria-label="Scroll right"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>
    </div>
  )
}

function RecipeCard({ item, isNew, onUse }: { item: RecipeItem; isNew?: boolean; onUse?: () => void }) {
  return (
    <div
      className="group relative flex-shrink-0 cursor-pointer overflow-hidden bg-gray-100 select-none"
      style={{ width: '200px', height: '200px', borderRadius: '28px' }}
    >
      <video
        loop
        autoPlay
        playsInline
        muted
        src={item.videoUrl}
        poster={item.posterUrl}
        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
      />
      
      {isNew && (
        <div className="absolute left-3 top-3 z-10 rounded-full border border-white/30 bg-white/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-md shadow-sm">
          New
        </div>
      )}

      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/20 transition-all duration-300 group-hover:bg-black/40">
        <h3 className="px-4 text-center text-xl font-bold leading-tight text-white drop-shadow-lg transition-transform duration-300 group-hover:-translate-y-4">
          {item.title}
        </h3>
        
        <div className="absolute bottom-5 translate-y-2 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onUse?.()
            }}
            className="rounded-full border border-white/40 bg-white/20 px-4 py-1.5 text-xs font-bold text-white backdrop-blur-md transition-all hover:bg-white/30 hover:border-white/60 cursor-pointer"
          >
            Use this recipe
          </button>
        </div>
      </div>
    </div>
  )
}